/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { Database } from "../database";

/**
 * Represents a user.
 * @class User
 */
export class User {
    /**
     * The unique identifier of the user.
     */
    userID: string;
    /**
     * The first name of the user.
     */
    firstName: string;
    /**
     * The last name of the user.
     */
    lastName: string;
    /**
     * The role of the user.
     */
    role: string;
    
    /**
     * Constructs a new User object.
     * @constructor
     * @param {string} userID - The unique identifier of the user.
     * @param {string} firstName - The first name of the user.
     * @param {string} lastName - The last name of the user.
     * @param {string} role - The role of the user.
     */
    constructor(userID: string, firstName: string, lastName: string, role: string) {
        this.userID = userID;
        this.firstName = firstName;
        this.lastName = lastName;
        this.role = role;
    }

    /**
     * Retrieves a list of users from the database.
     * @static
     * @async
     * @function list
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for database access.
     * @returns {Promise<User[]>} - A promise that resolves to an array of User objects.
     */
    static async list(database: Database, accessToken: string): Promise<User[]> {
        const users: User[] = [];
        if(database){
            const sql = "SELECT * FROM User;";
            const rows = await database.read(accessToken, sql);
            rows.forEach((row: any) => {
                const user = new User(row.userID, row.firstName, row.lastName, row.role);
                users.push(user);
                });
        }
        return users;
    }

    /**
     * Saves the new user to the database.
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for executing SQL statements in the database.
     * @return {Promise<void>} - A promise indicating the completion of the save operation.
     */
    async save(database: Database, accessToken: string): Promise<void> {
        const sql = `INSERT INTO User (UserID, FirstName, LastName, Role) VALUES ('${this.userID}','${this.firstName}','${this.lastName}','${this.role}')`;
        await database.readWrite(accessToken, sql);
    }

    /**
     * Deletes multiple users from the database based on their user IDs.
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for database access.
     * @param {Array<string>} selectedUserIDs - An array of user IDs to delete.
     */    
    static async deleteUsers(database: Database, accessToken: string, selectedUserIDs: Array<string>){
        const sql = `DELETE FROM User WHERE userID in ('${selectedUserIDs.join(
            "','"
        )}')`;
        database.readWrite(accessToken, sql);
    }
}